import { omit } from 'es-toolkit';
import BaseModel from './BaseModel';
import * as _ from 'es-toolkit';
import { v4 } from 'uuid';
import { Brick } from './Bricks';
import { Utils } from '../services';
import { normalizeBrickInputsMapping } from '../containers/Workflows/workflowInputsMappingUtils';
import {
    hydrateWorkflowBricksLayoutIds,
    persistBrickLayoutIdsForSave,
    reconcileWorkflowGraphReferences,
} from '../containers/Workflows/workflowFlowUtils';

/** Class representing a workflow. */
class Workflow extends BaseModel {
    static STATUS_PENDING = 'pending';
    static STATUS_RUNNING = 'running';
    static STATUS_WAITING = 'waiting';
    static STATUS_SUCCESS = 'success';
    static STATUS_FAILED  = 'failed';

    constructor(properties) {
        super(properties);

        if (this.bricks) {
            this.bricks = (this.bricks || []).map((props) => Brick.make({
                ...props,
                inputs_mapping: normalizeBrickInputsMapping(props?.inputs_mapping),
            }));
        }

        if (this.next)
            this.next = (this.next || []).map(props => Brick.make(props));

        if (this.editor_layout === undefined || this.editor_layout === null)
            this.editor_layout = {};

        if (Array.isArray(this.completion_message) || this.completion_message == null)
            this.completion_message = {};
        if (Array.isArray(this.description) || this.description == null)
            this.description = {};
        if (Array.isArray(this.name) || this.name == null)
            this.name = {};

        const hydrated = hydrateWorkflowBricksLayoutIds({
            bricks: this.bricks,
            editor_layout: this.editor_layout,
        });
        if (Array.isArray(hydrated.bricks))
            this.bricks = hydrated.bricks.map((props) => Brick.make(props));

        this.editor_layout = reconcileWorkflowGraphReferences(this.bricks, this.editor_layout);
    }

    /**
     * @override
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        if (Array.isArray(prepared.bricks)) {
            prepared.bricks = persistBrickLayoutIdsForSave(prepared.bricks)
                .map((brick) => ({
                    ...omit(brick, ['_clientLayoutId']),
                    inputs_mapping: normalizeBrickInputsMapping(brick?.inputs_mapping),
                }));
        }
        if (Array.isArray(prepared.bricks)) {
            prepared.editor_layout = reconcileWorkflowGraphReferences(
                prepared.bricks,
                prepared.editor_layout
            );
        }
        if (Array.isArray(prepared.runs_columns)) {
            prepared.runs_columns.forEach((column) => {
                Utils.cleanTranslatableProperty(column, 'label');
            });
        }
        return prepared;
    }

    /**
     * Get the current brick
     *
     * @return {Brick}
     */
    getCurrentBrick() {
        const brick_id = this.context?.__current_brick_id__;
        if (brick_id && Array.isArray(this.bricks)) {
            const match = this.bricks.find((brick) => {
                const candidates = [
                    brick?.id,
                    brick?._clientLayoutId,
                ].filter(Boolean).map((value) => String(value));
                return candidates.includes(String(brick_id));
            });
            if (match) {
                return match;
            }
        }

        const index = this.context?.__current__ || 0;
        return this.bricks[index] ?? null;
    }

    /**
     * Get the current brick index
     *
     * @return {Brick}
     */
    getCurrentBrickIndex() {
        return this.context?.__current__ || 0;
    }

    /**
     * Get the messages
     *
     * @return {String[]}
     */
    getMessages() {
        return this.context?.__messages__;
    }

    /**
     * Get the error messages
     *
     * @return {String[]}
     */
    getErrors() {
        return this.context?.__errors__;
    }

    /**
     * Check if this workflow is pending
     *
     * @return {Boolean}
     */
    isPending() {
        return this.context?.__status__ === Workflow.STATUS_PENDING;
    }

    /**
     * Check if this workflow is running
     *
     * @return {Boolean}
     */
    isRunning() {
        return this.context?.__status__ === Workflow.STATUS_RUNNING;
    }

    isWaiting() {
        return this.context?.__status__ === Workflow.STATUS_WAITING;
    }

    /**
     * Check if this workflow has succeeded
     *
     * @return {Boolean}
     */
    succeeded() {
        return this.context?.__status__ === Workflow.STATUS_SUCCESS;
    }

    /**
     * Check if this workflow has failed
     *
     * @return {Boolean}
     */
    failed() {
        return this.context?.__status__ === Workflow.STATUS_FAILED;
    }

    /**
     * Prepare this object for duplicate.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * @return {Object}
     */
    prepareForDuplicate() {
        const prepared = _.cloneDeep(this);

        prepared.id = v4();
        delete prepared._id;
        delete prepared.context;
        delete prepared.next;
        delete prepared.created_at;
        delete prepared.updated_at;

        if (prepared.name && typeof prepared.name === 'object') {
            Object.keys(prepared.name).forEach((lang) => {
                if (prepared.name[lang]) {
                    prepared.name[lang] = `${String(prepared.name[lang]).trimEnd()} copy`;
                }
            });
        }

        const source_bricks = this.bricks || prepared.bricks || [];
        prepared.bricks = source_bricks.map((brick) => {
            const raw = _.cloneDeep(brick);
            delete raw.id;
            delete raw.workflow_id;
            delete raw.created_at;
            delete raw.updated_at;
            delete raw.inputs;
            delete raw.outputs;
            delete raw.tkt_instance_id;

            return raw;
        });

        return new Workflow(prepared).prepareForUpdate();
    }

}

export default Workflow;
